import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CalendarClock, Ellipsis, KeyRound, LockKeyhole, Plus, RefreshCw, Undo2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { rentalsQuery, settingsQuery, type Rental } from '@/api/access'
import { clientName, clientNamesQuery, type ClientLite } from '@/api/passes'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import { LockerBoard } from '@/app/access/locker-board'
import { EndRentalDialog, RenewPanel, RentalPanel, RentPanel } from '@/app/access/rental-panels'
import { useNow } from '@/app/access/use-now'
import { localDate } from '@/domain/access'
import { errorText } from '@/domain/errors'
import { formatMoney } from '@/domain/pass-rules'
import { byUntil, endsSoon, shortDate } from '@/domain/rentals'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { NeutralSegmented } from '@/ui/segmented'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/entry/lockers')({
  component: Lockers,
})

function Lockers() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="lockers" allowed={(can) => can.visits}>
      {(ctx) => <LockersScreen key={ctx.branch.id} {...ctx} />}
    </EntryShell>
  )
}

type Filter = 'active' | 'ending' | 'ended'

/// What is open over the list: renting a locker out (from the button or a
/// free tile), one rental, its renewal, or the question before ending it.
type Overlay =
  | Readonly<{ kind: 'rent'; number: string | null }>
  | Readonly<{ kind: 'rental'; rental: Rental }>
  | Readonly<{ kind: 'renew'; rental: Rental }>
  | Readonly<{ kind: 'end'; rental: Rental }>

/// «Шафки» (specs/access.md §10b): the rentals — «Діють», «Закінчуються за
/// 7 днів», «Завершені» — and, with numbered keys, the board: who holds which
/// key and which lockers are somebody's for a while. `checkin ≥ view` reads;
/// renting out, renewing and ending are `checkin ≥ edit` (the server's rule).
function LockersScreen(ctx: EntryContext) {
  const { orgId, branch, timeZone, can } = ctx
  const s = t.access.lockers
  const navigate = useNavigate()
  const now = useNow(60_000)
  const today = localDate(now, timeZone)
  const settings = useQuery(settingsQuery(orgId, branch.id))
  const current = useQuery(rentalsQuery(orgId, branch.id, 'current'))
  const [filter, setFilter] = useState<Filter>('active')
  const ended = useQuery({ ...rentalsQuery(orgId, branch.id, 'ended'), enabled: filter === 'ended' })
  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const [news, setNews] = useState<string | null>(null)

  const lockers = settings.data?.lockers ?? []
  const mode = settings.data?.lockerMode
  const live = current.data ?? []
  const ending = live.filter(endsSoon)
  const rows = filter === 'ended' ? (ended.data ?? []) : filter === 'ending' ? ending : live.slice().sort(byUntil)

  const ids = [
    ...new Set([
      ...live.map((r) => r.clientId),
      ...(ended.data ?? []).map((r) => r.clientId),
      ...lockers.flatMap((l) => [l.clientId, l.rental?.clientId].filter((id): id is string => Boolean(id))),
    ]),
  ].sort()
  const names = useQuery(clientNamesQuery(orgId, ids))
  const clients = new Map((names.data ?? []).map((client) => [client.id, client]))
  const who = (clientId: string) => {
    const client = clients.get(clientId)
    return client ? clientName(client) : t.passes.sold.unknownClient
  }

  const done = (message: string) => {
    setOverlay(null)
    setNews(message)
  }

  if (settings.isPending) return <LoadingBlock label={t.app.loading} />
  if (settings.isError) {
    return <InlineBanner kind="error" message={errorText(settings.error)} action={{ label: t.app.retry, onClick: () => void settings.refetch() }} />
  }

  // A club with no lockers rents nothing out (§10b, mode `none`).
  if (mode === 'none' && live.length === 0) {
    return (
      <EmptyState
        icon={<LockKeyhole />}
        title={s.noLockers}
        message={s.noLockersMessage}
        action={
          can.setUp ? (
            <Button variant="secondary" onClick={() => void navigate({ to: '/c/$orgId/entry/settings', params: { orgId } })}>
              {s.openSettings}
            </Button>
          ) : undefined
        }
      />
    )
  }

  const left = (rental: Rental): ReactNode => {
    if (rental.status === 'active') {
      // Renewed: what matters is how long the chain runs, not this period.
      // A phone keeps the date alone — the renewal is the next row anyway.
      return rental.until && rental.lastDay && rental.until !== rental.lastDay ? (
        <>
          <span className="max-sm:hidden">{s.renewed}</span> {s.until(shortDate(rental.until, today))}
        </>
      ) : (
        s.left(rental.daysLeft ?? 0)
      )
    }
    if (rental.status === 'upcoming') return s.startsOn(shortDate(rental.startsOn, today))
    if (rental.status === 'cancelled') return s.cancelled
    return rental.endedOn ? s.endedOn(shortDate(rental.endedOn, today)) : s.over
  }

  const columns: readonly DataColumn<Rental>[] = [
    {
      key: 'locker',
      header: s.columns.locker,
      width: '88px',
      cell: (rental) => <span className="font-semibold text-text tabular">{s.number(rental.lockerNumber)}</span>,
    },
    {
      key: 'client',
      header: s.columns.client,
      width: 'minmax(0,1.4fr)',
      mobile: 'subtitle',
      // Inline under `sm`: the folded row joins its subtitle cells with «·».
      cell: (rental) => (
        <span className="min-w-0 items-center gap-2.5 sm:flex">
          <Avatar name={who(rental.clientId)} className="size-6 text-[10px] max-sm:hidden" />
          <span className="sm:truncate">{who(rental.clientId)}</span>
        </span>
      ),
    },
    {
      key: 'period',
      header: s.columns.period,
      width: 'minmax(0,1fr)',
      mobile: 'subtitle',
      cell: (rental) => (
        <span className="tabular">{s.period(shortDate(rental.startsOn, today), shortDate(rental.lastDay ?? rental.endsOn, today))}</span>
      ),
    },
    {
      key: 'left',
      // «Лишилось» means nothing on a rental that is over: there it is «Стан».
      header: filter === 'ended' ? s.columns.state : s.columns.left,
      width: 'minmax(0,1.1fr)',
      mobile: 'trailing',
      cell: (rental) => (
        <span className={rental.status === 'active' && endsSoon(rental) ? 'font-medium text-text' : 'text-text-2'}>{left(rental)}</span>
      ),
    },
    {
      key: 'price',
      header: s.columns.price,
      width: '112px',
      numeric: true,
      hideBelow: 'md',
      cell: (rental) => (rental.priceMinor > 0 ? formatMoney(rental.priceMinor) : <span className="text-muted">{s.free}</span>),
    },
  ]

  const counts = { active: live.length, ending: ending.length }
  const list = filter === 'ended' ? ended : current
  const rentButton = can.admit ? (
    <Button variant="primary" icon={<Plus className="size-4" strokeWidth={2.5} />} onClick={() => setOverlay({ kind: 'rent', number: null })}>
      {s.rent}
    </Button>
  ) : null

  return (
    <div className="flex flex-col gap-8">
      {mode === 'keys' ? <Figures lockers={lockers} /> : null}
      {news ? <InlineBanner kind="done" message={news} /> : null}

      <section className="flex flex-col gap-3" aria-label={s.rentals}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <NeutralSegmented
            label={s.filter}
            value={filter}
            onChange={setFilter}
            className="max-sm:w-full"
            options={[
              { value: 'active', label: counts.active ? `${s.filters.active} ${counts.active}` : s.filters.active },
              { value: 'ending', label: counts.ending ? `${s.filters.ending} ${counts.ending}` : s.filters.ending },
              { value: 'ended', label: s.filters.ended },
            ]}
          />
          {rentButton}
        </div>
        {list.isPending ? (
          <LoadingBlock label={t.app.loading} />
        ) : list.isError && !list.data ? (
          <InlineBanner kind="error" message={errorText(list.error)} action={{ label: t.app.retry, onClick: () => void list.refetch() }} />
        ) : rows.length === 0 ? (
          <div className="rounded-[16px] bg-surface">
            <EmptyState
              icon={filter === 'ended' ? <Undo2 /> : filter === 'ending' ? <CalendarClock /> : <KeyRound />}
              title={filter === 'ended' ? s.emptyEnded : filter === 'ending' ? s.emptyEnding : s.emptyActive}
              message={filter === 'active' ? s.emptyActiveMessage : undefined}
            />
          </div>
        ) : (
          <DataList
            label={s.rentals}
            columns={columns}
            rows={rows}
            rowKey={(rental) => rental.id}
            rowLabel={(rental) => `${s.number(rental.lockerNumber)}, ${who(rental.clientId)}`}
            rowTarget={(rental) => ({ onClick: () => setOverlay({ kind: 'rental', rental }) })}
            rowTone={(rental) => (rental.status === 'cancelled' ? 'muted' : 'normal')}
            rowActions={
              can.admit
                ? (rental) => (
                    <RowMenu label={`${t.app.more}: ${s.number(rental.lockerNumber)}`} trigger={<Ellipsis className="size-[18px]" />}>
                      <RowMenuItem icon={<RefreshCw className="size-4" />} onSelect={() => setOverlay({ kind: 'renew', rental })}>
                        {t.access.lockers.details.renew}
                      </RowMenuItem>
                      {rental.status === 'active' || rental.status === 'upcoming' ? (
                        <RowMenuItem destructive icon={<Undo2 className="size-4" />} onSelect={() => setOverlay({ kind: 'end', rental })}>
                          {rental.status === 'upcoming' ? t.access.lockers.details.cancel : t.access.lockers.details.end}
                        </RowMenuItem>
                      ) : null}
                    </RowMenu>
                  )
                : undefined
            }
          />
        )}
        {filter === 'ending' && rows.length > 0 ? <p className="px-4 text-caption text-muted">{s.endingHint}</p> : null}
        {mode === 'self' ? <p className="px-4 text-caption text-muted">{s.selfHint}</p> : null}
      </section>

      {mode === 'keys' ? (
        <LockerBoard
          lockers={lockers}
          clients={clients}
          today={today}
          canRent={can.admit}
          onRent={(number) => setOverlay({ kind: 'rent', number })}
          onOpen={(rentalId) => {
            const rental = live.find((r) => r.id === rentalId)
            if (rental) setOverlay({ kind: 'rental', rental })
          }}
        />
      ) : null}

      {overlay?.kind === 'rent' ? (
        <RentPanel
          orgId={orgId}
          branchId={branch.id}
          mode={mode === 'self' ? 'self' : 'keys'}
          lockers={lockers}
          taken={live}
          today={today}
          number={overlay.number}
          onClose={() => setOverlay(null)}
          onDone={(rental, client: ClientLite) => done(s.form.done(rental.lockerNumber, clientName(client)))}
        />
      ) : null}
      {overlay?.kind === 'rental' ? (
        <RentalPanel
          orgId={orgId}
          rental={overlay.rental}
          name={who(overlay.rental.clientId)}
          canEdit={can.admit}
          onClose={() => setOverlay(null)}
          onRenew={() => setOverlay({ kind: 'renew', rental: overlay.rental })}
          onEnd={() => setOverlay({ kind: 'end', rental: overlay.rental })}
        />
      ) : null}
      {overlay?.kind === 'renew' ? (
        <RenewPanel
          orgId={orgId}
          rental={overlay.rental}
          name={who(overlay.rental.clientId)}
          onClose={() => setOverlay(null)}
          onDone={(next) => done(s.renew.done(next.lockerNumber, shortDate(next.until ?? next.endsOn, today)))}
        />
      ) : null}
      <EndRentalDialog
        orgId={orgId}
        rental={overlay?.kind === 'end' ? overlay.rental : null}
        keys={mode === 'keys'}
        onClose={() => setOverlay(null)}
        onDone={(rental) => done(rental.status === 'cancelled' ? s.end.cancelled(rental.lockerNumber) : s.end.done(rental.lockerNumber))}
      />
    </div>
  )
}

/// Free, out and rented keys at a glance — the numbers the page leads with.
function Figures({ lockers }: { lockers: readonly { state: string; rental?: unknown }[] }) {
  const s = t.access.lockers.figures
  const free = lockers.filter((l) => l.state === 'free' && !l.rental).length
  const issued = lockers.filter((l) => l.state === 'issued').length
  const rented = lockers.filter((l) => l.rental).length
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-4">
      {[
        [s.free, free],
        [s.issued, issued],
        [s.rented, rented],
      ].map(([label, value]) => (
        <div key={label} className="flex flex-col">
          <span className="text-caption font-medium text-muted">{label}</span>
          <span className="text-[44px] leading-[52px] font-bold tracking-[-0.02em] text-text tabular">{value}</span>
        </div>
      ))}
    </div>
  )
}
